import { ArrowLeftIcon } from "@radix-ui/react-icons";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

import { Banner } from "@/components/Callout";
import { Page } from "@/components/Page";
import { day, ThrottleLog, UsageChart } from "@/components/RateLimitUsage";
import { Badge } from "@/components/ui/badge";
import { Button, ButtonLink } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { ViewFeedHeader } from "@/components/ViewFeedHeader";
import {
  createQuotaGrant,
  feedQueryOptions,
  feedRateLimitsQueryOptions,
  requestQuotaIncrease,
  revokeQuotaGrant,
  withdrawQuotaIncrease,
} from "@/lib/api";
import type { FeedRateLimits, RateLimitResource } from "@/lib/types";

/**
 * Rate limits — what this feed spent on metered nodes, and the hours it ran out.
 *
 * The page exists because running out is SILENT by design. A feed over its budget answers
 * the node with whatever its author chose (by default: matches nothing), acks the batch
 * and carries on, so a throttled afternoon and an afternoon when nothing matched look
 * identical from outside. The one thing this page must never do, therefore, is render an
 * unreadable history as a confident zero — see the `available: false` branch.
 *
 * It is built for someone arriving DAYS later. The headline is not the current hour, which
 * is usually fine and always boring; it is what happened across the window, with the hours
 * that ran out listed as rows rather than left as dips in a chart nobody scrubs.
 */
export const Route = createFileRoute("/app/_sidebar-layout/feed/$feedId/rate-limits")({
  component: RouteComponent,
  loader: async ({ context: { queryClient }, params: { feedId } }) => {
    const id = parseInt(feedId);
    await Promise.all([
      queryClient.ensureQueryData(feedQueryOptions(id)),
      queryClient.ensureQueryData(feedRateLimitsQueryOptions(id)),
    ]);
  },
});

const RANGES = [
  { hours: 72, label: "3 days" },
  { hours: 24 * 7, label: "7 days" },
  { hours: 24 * 30, label: "30 days" },
] as const;

function RouteComponent() {
  const { feedId } = Route.useParams();
  const id = parseInt(feedId);
  const [hours, setHours] = useState<number>(72);

  const { data, isPending } = useQuery({
    ...feedRateLimitsQueryOptions(id, hours),
    placeholderData: keepPreviousData,
  });

  return (
    <Page>
      <ViewFeedHeader
        feedId={feedId}
        icon={
          <div className="h-full w-full flex items-center justify-center">
            <ButtonLink
              to="/app/feed/$feedId/view"
              params={{ feedId }}
              variant="ghost"
              size="icon"
            >
              <span className="sr-only">Back to feed</span>
              <ArrowLeftIcon />
            </ButtonLink>
          </div>
        }
      />

      <div className="mx-auto flex w-full max-w-3xl flex-col gap-8 p-4">
        <header className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold">Rate limits</h1>
          <p className="text-sm text-muted-foreground">
            Some nodes cost a GPU to run, so each gets its own hourly budget. They are
            separate and are never pooled — running out of one does not touch the other.
            This is what yours has used, and the hours it ran out.
          </p>
        </header>

        {isPending && !data ? (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-56 w-full" />
          </div>
        ) : data ? (
          <Content
            data={data}
            hours={hours}
            onHoursChange={setHours}
            feedId={feedId}
          />
        ) : null}
      </div>
    </Page>
  );
}

function Content({
  data,
  hours,
  onHoursChange,
  feedId,
}: {
  data: FeedRateLimits;
  hours: number;
  onHoursChange: (hours: number) => void;
  feedId: string;
}) {
  if (!data.available) {
    return <Unavailable data={data} feedId={feedId} />;
  }
  if (data.resources.length === 0) {
    return <NoMeteredNodes feedId={feedId} />;
  }

  return (
    <>
      {/* One block per budget. Never summed: a "post judged" and a "question asked" are
          different units, and a combined total would be meaningless.

          🔴 The asking and granting controls live INSIDE each block, not once below them.
          They used to sit here, reading the flat top-level mirror — which speaks for
          adjudication whenever a feed has it — so on a feed with both budgets the buttons
          quoted one budget's number while the author was running out of the other, and the
          grant they produced raised the budget that was never short. A control that changes
          one budget has to be rendered by that budget, or there is nothing to stop it. */}
      {data.resources.map((res) => (
        <ResourceSection
          key={res.resource}
          res={res}
          hours={hours}
          onHoursChange={onHoursChange}
          retention={data.retention_days}
          feedId={feedId}
          canGrant={data.can_grant}
        />
      ))}
    </>
  );
}

/** Everything about one budget: what it is, what it has spent, and which nodes spend it. */
function ResourceSection({
  res,
  hours,
  onHoursChange,
  retention,
  feedId,
  canGrant,
}: {
  res: RateLimitResource;
  hours: number;
  onHoursChange: (hours: number) => void;
  retention: number;
  feedId: string;
  canGrant: boolean;
}) {
  const throttled = res.history.filter((w) => w.throttled);
  const pct = Math.min(100, Math.round(res.current.utilisation * 100));

  return (
    <section className="flex flex-col gap-3 rounded-lg border border-slatea-normal p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-lg font-semibold">{res.label}</h2>
        <span className="text-sm text-muted-foreground tabular-nums">
          {res.current.scored.toLocaleString()} of{" "}
          {res.current.limit.toLocaleString()} {res.unit} this hour
        </span>
      </div>

      <div className="h-2 w-full overflow-hidden rounded-full bg-slatea-normal">
        <div
          className={
            res.current.status === "throttled"
              ? "h-full rounded-full bg-crimson-normal"
              : res.current.status === "near"
                ? "h-full rounded-full bg-amber-normal"
                : "h-full rounded-full bg-cyan-normal"
          }
          style={{ width: `${pct}%` }}
        />
      </div>

      {/* Only say something when there IS something. A healthy budget needs no paragraph.
          Two, not one: the structural finding ("nothing is narrowing this node") and the
          historical one ("you lost 3M posts") are both true of the same feed and neither
          substitutes for the other — the first says what to change, the second says why. */}
      {res.guidance.slice(0, 2).map((finding) => (
        <Banner
          key={finding.code}
          variant={finding.severity === "warning" ? "warning" : "default"}
        >
          <span className="text-sm font-medium">{finding.message}</span>
        </Banner>
      ))}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm text-muted-foreground">Hour by hour</span>
        <div className="flex gap-1">
          {RANGES.map((range) => (
            <Button
              key={range.hours}
              size="sm"
              variant={hours === range.hours ? "outline" : "ghost"}
              onPress={() => onHoursChange(range.hours)}
            >
              {range.label}
            </Button>
          ))}
        </div>
      </div>

      {res.history.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Nothing spent in this window.
        </p>
      ) : (
        <UsageChart history={res.history} limit={res.limit.units} />
      )}

      <ThrottleLog windows={throttled} nodes={res.nodes} retention={retention} />
      <NodeBreakdown res={res} hours={hours} feedId={feedId} />
      <AskForMore res={res} canGrant={canGrant} feedId={feedId} />
      <CarveOuts res={res} canGrant={canGrant} feedId={feedId} />
    </section>
  );
}

function NodeBreakdown({
  res,
  hours,
  feedId,
}: {
  res: RateLimitResource;
  hours: number;
  feedId: string;
}) {
  if (res.nodes.length === 0) return null;

  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm text-muted-foreground">
        {res.nodes.length === 1 ? "The node spending it" : "The nodes spending it"}
      </span>
      {res.nodes.map((node, i) => (
        <div
          key={node.rule_id ?? `${node.operator}-${i}`}
          className="flex flex-wrap items-baseline justify-between gap-2 rounded-md border border-slatea-normal p-3"
        >
          <span className="text-sm">
            “{node.predicate}”{" "}
            <span className="text-muted-foreground">
              {node.comparator} {node.threshold}
              {node.detail ? ` · ${node.detail}` : ""}
            </span>
          </span>
          {/* Null, not zero: for questions the id cannot be matched, and "0 judged" would
              be a lie rather than an absence. */}
          {typeof node.scored === "number" && (
            <span className="text-sm text-muted-foreground tabular-nums">
              {node.scored.toLocaleString()} in {hours}h
              {node.refused ? `, ${node.refused.toLocaleString()} skipped` : ""}
            </span>
          )}
        </div>
      ))}
      <ButtonLink
        to="/app/feed/$feedId/edit"
        params={{ feedId }}
        variant="outline"
        size="sm"
        className="self-start"
      >
        Edit this feed
      </ButtonLink>
    </div>
  );
}
function Unavailable({
  data,
  feedId,
}: {
  data: Extract<FeedRateLimits, { available: false }>;
  feedId: string;
}) {
  return (
    <section className="flex flex-col gap-4">
      <Banner variant="warning">
        <div className="flex flex-col gap-1">
          <span className="font-medium">
            Usage history is not available right now.
          </span>
          <span className="text-xs">
            {data.reason === "no_history_sink"
              ? "This deployment is not recording it."
              : "We could not read it. Your feed is unaffected — the limits still apply exactly as before, we just cannot show you the history."}
          </span>
        </div>
      </Banner>
      {data.nodes.length > 0 && (
        <p className="text-sm text-muted-foreground">
          This feed has {data.nodes.length} metered node
          {data.nodes.length === 1 ? "" : "s"}.
        </p>
      )}
      <ButtonLink
        to="/app/feed/$feedId/view"
        params={{ feedId }}
        variant="outline"
        size="sm"
        className="self-start"
      >
        Back to the feed
      </ButtonLink>
    </section>
  );
}

function NoMeteredNodes({ feedId }: { feedId: string }) {
  return (
    <section className="flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        This feed has no rate-limited nodes, so it has no budget to run out of. Two nodes
        cost a GPU and are metered separately — Natural Language, charged per post it
        judges, and Ask a Question, charged per question it asks about a post. Everything
        else your feed does is unmetered.
      </p>
      <ButtonLink
        to="/app/feed/$feedId/edit"
        params={{ feedId }}
        variant="outline"
        size="sm"
        className="self-start"
      >
        Edit this feed
      </ButtonLink>
    </section>
  );
}

/**
 * Authorized carve-outs from the default budget.
 *
 * Visible to the owner, grantable only by an admin — and which of those you are is decided by
 * the server (`can_grant`), never here. An owner seeing the grant matters: without it the
 * number on this page and the number their feed is metered at would disagree with no way to
 * tell, which is the confusion the whole page exists to end.
 *
 * Revoked and expired grants stay listed. "We used to have a higher limit and it ended in
 * March" is the first thing worth knowing when a feed starts going quiet.
 */

/**
 * The author's way to ask for a bigger budget.
 *
 * Shown to everyone with a metered node, not only to feeds that have already been
 * throttled: an author who can see they are at 80% should be able to say so BEFORE their
 * feed goes quiet, which is the only point at which asking is cheap for everybody.
 *
 * Admins get the carve-out form instead — they do not need to ask themselves.
 */
function AskForMore({
  res,
  canGrant,
  feedId,
}: {
  res: RateLimitResource;
  canGrant: boolean;
  feedId: string;
}) {
  const [open, setOpen] = useState(false);
  const [units, setUnits] = useState("");
  const [reason, setReason] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const id = parseInt(feedId);
  // This budget's own request, not "any request this feed has open". An author can be
  // waiting on a decision about questions while their post budget is untouched.
  const existing = res.increase_request;
  // What this budget is counted in — "posts", "questions". Every number and every input
  // label below is in these units, because the two budgets do not share one.
  const unit = res.unit_short;
  const Unit = unit.charAt(0).toUpperCase() + unit.slice(1);

  if (canGrant) return null;

  if (existing && existing.status === "open") {
    return (
      <section className="flex flex-col gap-2 rounded-lg border border-border p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold">Your request is with us</h2>
          <Badge variant="outline">Waiting for a decision</Badge>
        </div>
        <p className="text-sm text-muted-foreground">
          You asked for{" "}
          {existing.requested_units
            ? `${existing.requested_units.toLocaleString()} ${unit} an hour`
            : "a higher limit"}{" "}
          for {res.label}. Nothing changes until we answer — this budget is still{" "}
          {res.limit.units.toLocaleString()} {unit} an hour in the meantime.
        </p>
        <p className="text-sm">“{existing.reason}”</p>
        <Button
          size="sm"
          variant="ghost"
          className="self-start"
          disabled={pending}
          // `onPress` wants a void return, so the promise is started and explicitly
          // discarded rather than handed back. Passing an async function returns a promise
          // nothing awaits -- a rejection escaping the try/finally would be an unhandled
          // rejection rather than an error this component can show.
          onPress={() => {
            void (async () => {
              setPending(true);
              try {
                await withdrawQuotaIncrease(id, existing.id);
              } catch {
                setError("That did not go through.");
              } finally {
                setPending(false);
              }
            })();
          }}
        >
          Withdraw
        </Button>
        {error && <p className="text-sm text-destructive">{error}</p>}
      </section>
    );
  }

  if (existing && existing.status === "declined") {
    return (
      <section className="flex flex-col gap-2 rounded-lg border border-border p-4">
        <h2 className="text-sm font-semibold">We could not raise this one</h2>
        {existing.resolution_note && (
          <p className="text-sm text-muted-foreground">{existing.resolution_note}</p>
        )}
        <Button size="sm" variant="outline" className="self-start" onPress={() => setOpen(true)}>
          Ask again
        </Button>
      </section>
    );
  }

  if (!open) {
    return (
      <div className="flex flex-wrap items-center gap-3">
        <Button size="sm" variant="outline" onPress={() => setOpen(true)}>
          Ask for more {unit}
        </Button>
        <span className="text-sm text-muted-foreground">
          {res.current.status === "throttled"
            ? `This feed has run out of ${res.label} budget. Tell us what it needs.`
            : res.limit.source === "default"
              ? `On the standard ${res.limit.units.toLocaleString()} ${unit} an hour.`
              : `On ${res.limit.units.toLocaleString()} ${unit} an hour.`}
        </span>
      </div>
    );
  }

  return (
    <section className="flex flex-col gap-3 rounded-lg border border-border p-4">
      <h2 className="text-sm font-semibold">Ask for more {res.label} budget</h2>
      <p className="text-sm text-muted-foreground">
        We read these. Tell us what the feed is for and roughly how much it needs — this
        budget&rsquo;s recent usage comes with the request, so you do not have to quote
        numbers.
      </p>
      <div className="flex flex-wrap gap-2">
        <Input
          aria-label={`${Unit} per hour`}
          placeholder={`${Unit} per hour (optional)`}
          value={units}
          onChange={(event) => setUnits(event.target.value)}
          className="w-52"
        />
        <Input
          aria-label="Why"
          placeholder="Why this feed needs more"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          className="flex-1 min-w-64"
        />
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      <div className="flex gap-2">
        <Button
          size="sm"
          disabled={pending}
          // See the note on Withdraw above: started and discarded, not returned.
          onPress={() => {
            void (async () => {
              if (!reason.trim()) {
                setError("Tell us why, even briefly — that is what we read.");
                return;
              }
              const parsed = parseInt(units, 10);
              if (units.trim() && (!Number.isFinite(parsed) || parsed <= 0)) {
                setError(`${Unit} per hour has to be a positive number, or leave it empty.`);
                return;
              }
              setPending(true);
              setError(null);
              try {
                await requestQuotaIncrease(id, {
                  // The budget this block speaks for. Sending nothing used to file the
                  // request against `adjudication` whatever the author was short of.
                  resource: res.resource,
                  reason: reason.trim(),
                  requested_units: units.trim() ? parsed : undefined,
                });
                setOpen(false);
                setUnits("");
                setReason("");
              } catch {
                setError("That did not go through. Nothing was sent.");
              } finally {
                setPending(false);
              }
            })();
          }}
        >
          Send request
        </Button>
        <Button size="sm" variant="ghost" onPress={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
    </section>
  );
}

export function CarveOuts({
  res,
  canGrant,
  feedId,
}: {
  res: RateLimitResource;
  canGrant: boolean;
  feedId: string;
}) {
  const [units, setUnits] = useState("");
  const [reason, setReason] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const id = parseInt(feedId);
  // Only this budget's carve-outs. The top-level list mixes both, and a grant listed under
  // the wrong heading reads as "your limit was raised" to someone whose limit was not.
  const grants = res.grants ?? [];
  const unit = res.unit_short;
  const Unit = unit.charAt(0).toUpperCase() + unit.slice(1);

  if (!canGrant && grants.length === 0) {
    return null;
  }

  const submit = async () => {
    const parsed = parseInt(units, 10);
    if (!Number.isFinite(parsed) || parsed <= 0 || !reason.trim()) {
      setError("A carve-out needs a budget and a reason.");
      return;
    }
    setPending(true);
    setError(null);
    try {
      await createQuotaGrant(id, {
        // 🔴 The budget being raised. Omitting this is what made granting a question-only
        // feed a no-op that looked like a success.
        resource: res.resource,
        units: parsed,
        window_secs: res.limit.window_secs || 3600,
        reason: reason.trim(),
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : undefined,
      });
      setUnits("");
      setReason("");
      setExpiresAt("");
    } catch {
      setError("That did not go through. Nothing was changed.");
    } finally {
      setPending(false);
    }
  };

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-sm font-semibold">{res.label} carve-outs</h2>

      {grants.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          This feed is on the standard {res.limit.units.toLocaleString()} {unit} an hour for{" "}
          {res.label}.
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {grants.map((grant) => (
            <div
              key={grant.id}
              className="flex flex-wrap items-start justify-between gap-2 rounded-lg border border-slatea-normal p-3"
            >
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium tabular-nums">
                  {grant.units.toLocaleString()} {unit} an hour
                  {grant.expires_at && !grant.revoked_at
                    ? ` until ${day(Date.parse(grant.expires_at) / 1000)}`
                    : ""}
                </span>
                <span className="text-sm text-muted-foreground">{grant.reason}</span>
                <span className="text-xs text-muted-foreground">
                  {grant.created_at ? day(Date.parse(grant.created_at) / 1000) : "—"}
                  {grant.revoked_at
                    ? ` · revoked ${day(Date.parse(grant.revoked_at) / 1000)}`
                    : ""}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={grant.active ? "success" : "secondary"}>
                  {grant.active ? "In force" : grant.revoked_at ? "Revoked" : "Expired"}
                </Badge>
                {canGrant && grant.active && (
                  <Button
                    size="sm"
                    variant="outline"
                    onPress={() => void revokeQuotaGrant(id, grant.id)}
                  >
                    Revoke
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {canGrant && (
        <div className="flex flex-col gap-2 rounded-lg border border-dashed border-slatea-normal p-3">
          <span className="text-sm font-medium">
            Authorize more {res.label} budget
          </span>
          <div className="flex flex-wrap gap-2">
            <Input
              className="w-40"
              inputMode="numeric"
              aria-label={`${Unit} per hour`}
              placeholder={`${Unit} per hour`}
              value={units}
              onChange={(e) => setUnits(e.target.value)}
            />
            <Input
              className="flex-1 min-w-60"
              placeholder="Why (recorded against your name)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <Input
              className="w-44"
              type="date"
              aria-label="Expires (optional)"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
            />
            <Button size="sm" onPress={() => void submit()} disabled={pending}>
              {pending ? "Granting…" : "Grant"}
            </Button>
          </div>
          {error && <span className="text-xs text-red-normal">{error}</span>}
          <span className="text-xs text-muted-foreground">
            Raises {res.label} only — the feed&rsquo;s other budgets are unchanged. Takes
            effect within a few minutes. Leave the date empty for a carve-out that stands
            until someone revokes it.
          </span>
        </div>
      )}
    </section>
  );
}
